// Fiche produit (refonte 2026), porté de src/sections/pdp.js du prototype : galerie en mosaïque, sections
// dépliables (une ouverte à la fois), quantité, ajout au panier Shopify, barre « Ajouter au panier »
// qui suit le défilement.
import { createDissolve, drawCover, drawContain, pixelImage } from '@oqp/dissolve';

const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
const loaded = img => img.complete && img.naturalWidth ? Promise.resolve(img) : new Promise((ok, ko) => {
  img.loading = 'eager';
  img.addEventListener('load', () => ok(img), { once: true });
  img.addEventListener('error', ko, { once: true });
});

// Galerie : les flèches changent d'image avec la même transition en mosaïque que les cartes produit
function initGallery(root) {
  const stage = root.querySelector('.gallery__stage');
  const imgs = [...root.querySelectorAll('.gallery__img')];
  const status = root.querySelector('[data-gallery-status]');
  if (imgs.length < 2) return;
  let index = 0, busy = false, dissolve = null, px = [], ready = null;

  // détouré en entier dans son cadre sur fond blanc, photos plein cadre
  const painter = i => (ctx, w, h) => {
    const img = imgs[i], s = w / stage.clientWidth;
    ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, w, h);
    if (img.classList.contains('is-shot')) drawContain(ctx, px[i], img.offsetLeft * s, img.offsetTop * s, img.offsetWidth * s, img.offsetHeight * s);
    else drawCover(ctx, px[i], 0, 0, w, h);
  };

  const setup = () => ready ||= (async () => {
    try {
      await Promise.all(imgs.map(loaded));
      px = await Promise.all(imgs.map(pixelImage));
      const canvas = document.createElement('canvas');
      canvas.className = 'gallery__canvas';
      canvas.setAttribute('aria-hidden', 'true');
      stage.appendChild(canvas);
      dissolve = createDissolve(canvas, painter(0), painter(1), { duration: reduced ? 1 : 450, style: 'mosaic' });
      if (!dissolve) canvas.remove();
    } catch (e) {
      dissolve = null;   // image inaccessible (CORS…) : changement d'image sans transition
    }
  })();

  // les demandes qui arrivent pendant une transition attendent (la dernière gagne) ; les flèches partent de
  // la dernière image demandée, sinon trois clics rapides n'avancent que d'une image
  let queued = null, wanted = 0;
  async function show(target) {
    wanted = target;
    if (busy) { queued = target; return; }
    if (target === index) return;
    busy = true;
    await setup();
    if (dissolve) {
      dissolve.setImages(painter(index), painter(target));
      dissolve.to(1);
      await new Promise(r => setTimeout(r, reduced ? 0 : 470));
    }
    imgs[index].classList.remove('is-active');
    imgs[target].classList.add('is-active');
    index = target;
    if (status) status.textContent = `Image ${index + 1} sur ${imgs.length}`;
    busy = false;
    if (queued !== null) { const q = queued; queued = null; show(q); }
  }
  const go = dir => { hovering = false; show((wanted + dir + imgs.length) % imgs.length); };

  // survol : la photo arrive comme sur les cartes de la home, le produit revient à la sortie
  const photo = imgs.findIndex(img => img.classList.contains('is-photo'));
  let hovering = false;
  const fine = matchMedia('(hover: hover)');
  root.addEventListener('pointerenter', e => {
    if (e.pointerType !== 'mouse' || !fine.matches || photo < 0 || index !== 0) return;
    hovering = true; show(photo);
  });
  root.addEventListener('pointerleave', () => { if (hovering) { hovering = false; show(0); } });
  // préparer les textures dès que la galerie approche, pour que le 1er survol soit fluide
  new IntersectionObserver((entries, io) => {
    if (entries.some(e => e.isIntersecting)) { io.disconnect(); setTimeout(setup, 600); }
  }).observe(root);

  root.querySelector('.gallery__nav--prev')?.addEventListener('click', () => go(-1));
  root.querySelector('.gallery__nav--next')?.addEventListener('click', () => go(1));
  root.addEventListener('keydown', e => { if (e.key === 'ArrowLeft') go(-1); if (e.key === 'ArrowRight') go(1); });
}

// Sections dépliables : une seule ouverte à la fois, hauteur animée
function initAccordion(root) {
  const items = [...root.querySelectorAll('.acc')];
  const animate = (panel, open) => {
    if (reduced) { panel.hidden = !open; return; }
    panel.hidden = false;
    const h = panel.scrollHeight;
    const anim = panel.animate(open ? [{ height: '0px' }, { height: h + 'px' }] : [{ height: h + 'px' }, { height: '0px' }],
      { duration: 320, easing: 'cubic-bezier(.4, 0, .2, 1)' });
    anim.onfinish = () => { panel.hidden = !open; };
  };
  const set = (item, open) => {
    if (item.classList.contains('is-open') === open) return;
    item.classList.toggle('is-open', open);
    item.querySelector('.acc__btn').setAttribute('aria-expanded', String(open));
    animate(item.querySelector('.acc__panel'), open);
  };
  items.forEach(item => item.querySelector('.acc__btn').addEventListener('click', () => {
    const open = !item.classList.contains('is-open');
    items.forEach(other => set(other, other === item ? open : false));
  }));
}

// Ajout au panier par l'action standard Shopify : le tiroir panier d'Horizon s'ouvre et se met à jour
async function addLines(form, quantity) {
  const data = new FormData(form);
  const line = { merchandiseId: String(data.get('id')), quantity };
  if (data.get('selling_plan')) line.sellingPlanId = String(data.get('selling_plan'));
  const attributes = [];
  for (const [k, v] of data) {
    const m = k.match(/^properties\[(.+)\]$/);
    if (m && v !== '') attributes.push({ key: m[1], value: String(v) });
  }
  if (attributes.length) line.attributes = attributes;

  if (window.Shopify?.actions?.updateCart) {
    await window.Shopify.actions.updateCart({ lines: [line] });
    return;
  }
  // secours : API AJAX du panier, puis la page panier
  const body = { items: [{ id: Number(line.merchandiseId), quantity, ...(line.sellingPlanId && { selling_plan: Number(line.sellingPlanId) }),
    ...(attributes.length && { properties: Object.fromEntries(attributes.map(a => [a.key, a.value])) }) }] };
  const r = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).description || 'Ajout impossible');
  location.href = `${window.Shopify?.routes?.root || '/'}cart`;
}

function initBuy(form) {
  const input = form.querySelector('.qty__input');
  const [minus, plus] = form.querySelectorAll('.qty__btn');
  const error = document.querySelector('[data-cart-error]');
  const clamp = v => Math.min(10, Math.max(1, Math.round(+v) || 1));
  const sync = () => { input.value = clamp(input.value); minus.disabled = +input.value <= 1; plus.disabled = +input.value >= 10; };
  form.querySelectorAll('[data-qty]').forEach(b => b.addEventListener('click', () => { input.value = clamp(+input.value + +b.dataset.qty); sync(); }));
  input.addEventListener('change', sync);
  sync();

  const label = form.querySelector('[data-cart-label]');
  let busy = false;
  form.addEventListener('submit', async e => {
    e.preventDefault();
    // le configurateur de carte rend le formulaire invalide tant qu'aucune carte n'est prête
    if (busy || !form.checkValidity()) return;
    busy = true;
    if (error) error.hidden = true;
    try {
      await addLines(form, form.querySelector('.qty')?.hidden ? 1 : clamp(input.value));
      if (label) {
        const idle = label.dataset.idle ||= label.textContent;
        label.textContent = label.dataset.done;
        setTimeout(() => { label.textContent = idle; }, 1800);
      }
    } catch (err) {
      if (error) { error.textContent = 'Oups, l’ajout au panier n’a pas marché. Réessaie dans un instant.'; error.hidden = false; }
    } finally {
      busy = false;
    }
  });
}

// Barre « Ajouter au panier » : là dès que le bouton principal n'est pas à l'écran (sur téléphone il est
// sous la photo, la barre est donc visible dès l'arrivée), cachée quand on le voit, s'efface au wallet
function initBuybar(form) {
  const bar = document.querySelector('[data-buybar]');
  if (!bar) return;
  // carte mots mêlés : le bouton d'ajout n'existe qu'une fois la carte créée, on suit le configurateur
  const main = document.querySelector('.pdp__buy custom-puzzle-creator') || form?.querySelector('.btn-cart') || form;
  const until = document.querySelector('#MainContent > .wallet-slot');
  if (!main) return;
  let shown = false;
  const update = () => {
    const h = innerHeight, r = main.getBoundingClientRect();
    const mainOnScreen = r.bottom > 0 && r.top < h - bar.offsetHeight - 24;   // pas caché sous la barre
    const on = !mainOnScreen && (!until || until.getBoundingClientRect().top > h * .6);
    if (on === shown) return;
    shown = on;
    bar.classList.toggle('is-in', on);
    bar.toggleAttribute('inert', !on);
    bar.setAttribute('aria-hidden', String(!on));
  };
  addEventListener('scroll', update, { passive: true });
  addEventListener('resize', update);
  update();

  const btn = bar.querySelector('[data-buybar-add]');
  let t = 0;
  btn?.addEventListener('click', async () => {
    if (btn.disabled || !form) return;
    btn.disabled = true;
    try {
      await addLines(form, 1);
      btn.classList.add('is-done');
      clearTimeout(t);
      t = setTimeout(() => btn.classList.remove('is-done'), 1800);
    } catch (e) { /* le message d'erreur est sous le bouton principal */ }
    btn.disabled = false;
  });
  // carte mots mêlés : la barre ouvre le configurateur, ou ajoute la carte quand elle est prête
  bar.querySelector('[data-buybar-configurator]')?.addEventListener('click', () => {
    const creator = document.querySelector('custom-puzzle-creator');
    if (creator?.hasAttribute('data-ready') && form) { form.requestSubmit(); return; }
    const open = creator?.querySelector('[data-entry] [data-act="open"]');
    if (open) open.click();
    else document.getElementById('produit')?.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth' });
  });
}

const form = document.querySelector('[data-oqp-product-form]');
document.querySelectorAll('[data-gallery]').forEach(initGallery);
document.querySelectorAll('[data-accordion]').forEach(initAccordion);
if (form) initBuy(form);
initBuybar(form);
